import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import queryString from 'query-string';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Spin, Tooltip } from 'antd';
import decodeHtml from '~helpers/decode-html';
import { communicationStatus } from '~constants';
import { propertyDraftStatus, propertyDraftCategory } from '~constants/pending-approval';
import {
  LogoCommentHeader as LogoCommentHeaderIcon,
  Video as VideoIcon,
  LogoCommentFooter as LogoCommentFooterIcon,
} from '~components/svgs';
import { imageSizes } from '~constants/gallery';
import { imageUrl, getLocale } from '~helpers/gallery';
import generatePath from '~settings/routing';
import * as actions from '~actions/pms-actions/pending-approval';

const mapStateToProps = state => {
  const data = state.pmsReducer.dashboard.pendingApproval.toJS();

  return {
    getPropertyCommentsStatus: data.communication.getComments.status,
    property: data.propertyComments,
  };
};

const mapDispatchToProps = dispatch => ({
  getPropertyComments: (id, successCallback) => {
    dispatch(actions.getPropertyComments(id, successCallback));
  },
});

@connect(mapStateToProps, mapDispatchToProps)
@withTranslation()
export default class Comments extends React.Component {
  constructor(props) {
    super(props);

    const { category } = queryString.parse(props.location.search);
    this.categoryArr = category ? [category] : ['DETAIL', 'GALLERY'];
    this.commentDrafts = null;
    this.detailDraft = null;
    this.photoDrafts = [];
    this.videoDrafts = [];
    this.linkDrafts = [];
  }
  componentDidMount() {
    const { propertySlug } = this.props.match.params;

    this.props.getPropertyComments(decodeURIComponent(propertySlug), () => {
      this.commentDrafts = this.getAvailableDrafts();

      if (!this.commentDrafts) {
        this.props.history.push(generatePath('property.homepage', { propertySlug }));
      } else {
        this.detailDraft = this.commentDrafts.find(
          draft => draft.category === propertyDraftCategory.DETAIL,
        );
        this.photoDrafts = this.commentDrafts.filter(draft => draft.image);
        this.videoDrafts = this.commentDrafts.filter(draft => draft.video);
        this.linkDrafts = this.commentDrafts.filter(
          draft => draft.propertyLink || draft.unitTypeLink,
        );
      }
    });
  }

  getAvailableDrafts = () => {
    const { property } = this.props;
    if (property && property.drafts && property.drafts.edges.length > 0) {
      const propertyDrafts = property.drafts.edges.map(draft => draft.node);
      let expiredDrafts = [];
      let rejectedDrafts = [];

      expiredDrafts = propertyDrafts.filter(draft => {
        const { category, status } = draft;
        return this.categoryArr.includes(category) && status === propertyDraftStatus.EXPIRED;
      });
      rejectedDrafts = propertyDrafts.filter(draft => {
        const { category, status } = draft;
        return this.categoryArr.includes(category) && status === propertyDraftStatus.REJECTED;
      });

      if (expiredDrafts.length > 0) {
        return expiredDrafts;
      }
      if (rejectedDrafts.length > 0) {
        return rejectedDrafts;
      }

      return null;
    }

    return null;
  };

  getHtmlOfTAndCElement = () => {
    const translatedTAndCs = this.props.t('cms:cms.draft.comments.footer.acceptance_message', {
      terms_and_condition: this.wrapALabel('/terms/terms-of-use', 'terms_and_conditions'),
      privacy_policy: this.wrapALabel('/terms/privacy', 'privacy_policy'),
    });
    return decodeHtml(translatedTAndCs);
  };

  wrapALabel = (linkParam, contentKey) => {
    const link = `https://www.student.com${linkParam}`;
    const content = this.props.t(`cms:cms.draft.comments.footer.${contentKey}`);
    return `<a target="_blank" href="${link}">${content}</a>`;
  };

  getRichComments = comments => comments.map(item => item.comment).join('');

  render() {
    const { t, getPropertyCommentsStatus } = this.props;

    return (
      <div className="comments">
        <Choose>
          <When condition={getPropertyCommentsStatus !== communicationStatus.IDLE}>
            <div className="comments__loading">
              <Spin />
            </div>
          </When>
          <Otherwise>
            <div className="comments__container">
              <div className="comments__header">
                <LogoCommentHeaderIcon className="comments__logo" />
              </div>
              <div className="comments__main">
                <div>{t('cms:cms.draft.comments.greetings1')}</div>
                <If
                  condition={
                    this.commentDrafts &&
                    this.commentDrafts.length > 0 &&
                    this.commentDrafts[0].status === propertyDraftStatus.EXPIRED
                  }
                >
                  <div>{t('cms:cms.draft.comments.apologizing1')}</div>
                  <div className="comments__greeting">
                    {t('cms:cms.draft.comments.apologizing2')}
                  </div>
                  <div className="comments__greeting-main">
                    {t('cms:cms.draft.comments.apologizing3')}
                  </div>
                </If>
                <If
                  condition={
                    this.commentDrafts &&
                    this.commentDrafts.length > 0 &&
                    this.commentDrafts[0].status === propertyDraftStatus.REJECTED
                  }
                >
                  <div className="comments__greeting">{t('cms:cms.draft.comments.greetings2')}</div>
                  <div className="comments__greeting-main">
                    {t('cms:cms.draft.comments.greetings3')}
                  </div>
                  <div className="comments__wrap">
                    <If condition={this.detailDraft}>
                      <div className="comments__category-wrap">
                        <div className="comments__type-title">
                          {t('cms:cms.pending_approval.comments.title.details')}
                        </div>
                        <div
                          className="comments__content-wrap"
                          dangerouslySetInnerHTML={{
                            __html: this.getRichComments(this.detailDraft.comments),
                          }}
                        />
                      </div>
                    </If>
                    <If condition={this.photoDrafts.length > 0}>
                      <div className="comments__category-wrap">
                        <div className="comments__type-title">
                          {t('cms:cms.pending_approval.comments.title.photo')}
                        </div>
                        <For each="photoDraft" of={this.photoDrafts}>
                          <div key={photoDraft.id} className="comments__draft-image-wrap">
                            <img
                              src={imageUrl(photoDraft.image, imageSizes.big)}
                              alt={photoDraft.image.filename || ''}
                              className="comments__draft-image"
                            />
                            <span
                              className="comments__image-comment"
                              dangerouslySetInnerHTML={{
                                __html: this.getRichComments(photoDraft.comments),
                              }}
                            />
                          </div>
                        </For>
                      </div>
                    </If>
                    <If condition={this.videoDrafts.length > 0}>
                      <div className="comments__category-wrap">
                        <div className="comments__type-title">
                          {t('cms:cms.pending_approval.comments.title.video')}
                        </div>
                        <For each="videoDraft" of={this.videoDrafts}>
                          <div key={videoDraft.id} className="comments__draft-video-wrap">
                            <div className="comments__video-item">
                              <span className="comments__video-tag">
                                <VideoIcon className="comments__video-icon" />
                                {t('cms:cms.pending_approval.tag.video')}
                              </span>
                              <span className="comments__label-wrap">
                                <span>{t('cms:cms.pending_approval.label.video_link')}</span>
                                <span>{t('cms:cms.pending_approval.label.locale')}</span>
                              </span>
                              <span className="comments__text-wrap">
                                <Tooltip
                                  placement="top"
                                  title={
                                    videoDraft.video && videoDraft.video.links
                                      ? videoDraft.video.links.hqMp4
                                      : ' '
                                  }
                                >
                                  <span>
                                    {videoDraft.video && videoDraft.video.links
                                      ? videoDraft.video.links.hqMp4
                                      : ' '}
                                  </span>
                                </Tooltip>
                                <span>{getLocale(videoDraft.video.locales)}</span>
                              </span>
                            </div>
                            <div
                              className="comments__video-comment"
                              dangerouslySetInnerHTML={{
                                __html: this.getRichComments(videoDraft.comments),
                              }}
                            />
                          </div>
                        </For>
                      </div>
                    </If>
                    <If condition={this.linkDrafts.length > 0}>
                      <div className="comments__category-wrap">
                        <div className="comments__type-title">
                          {t('cms:cms.pending_approval.comments.title.link')}
                        </div>
                        <For each="linkDraft" of={this.linkDrafts}>
                          <div key={linkDraft.id} className="comments__draft-link-wrap">
                            <div className="comments__link-item">
                              <span className="comments__label-wrap">
                                <span>{t('cms:cms.pending_approval.label.link_address')}</span>
                                <span>{t('cms:cms.pending_approval.label.locale')}</span>
                              </span>
                              <span className="comments__text-wrap">
                                <Choose>
                                  <When condition={linkDraft && linkDraft.propertyLink}>
                                    <Tooltip
                                      placement="top"
                                      title={linkDraft.propertyLink.link || ''}
                                    >
                                      <span>{linkDraft.propertyLink.link || ''}</span>
                                    </Tooltip>
                                  </When>
                                  <When condition={linkDraft && linkDraft.unitTypeLink}>
                                    <Tooltip
                                      placement="top"
                                      title={linkDraft.unitTypeLink.link || ''}
                                    >
                                      <span>{linkDraft.unitTypeLink.link || ''}</span>
                                    </Tooltip>
                                  </When>
                                </Choose>
                                <span>ALL</span>
                              </span>
                            </div>
                            <div
                              className="comments__link-comment"
                              dangerouslySetInnerHTML={{
                                __html: this.getRichComments(linkDraft.comments),
                              }}
                            />
                          </div>
                        </For>
                      </div>
                    </If>
                  </div>
                </If>
                <div className="comments__greeting-regards">
                  {t('cms:cms.draft.comments.greetings4')}
                </div>
                <div className="comments__greeting-signature">
                  {t('cms:cms.draft.comments.greetings5')}
                </div>
              </div>
              <div className="comments__footer">
                <LogoCommentFooterIcon className="comments__footer-logo" />
                <div className="comments__footer-copyright">
                  {t('cms:cms.draft.comments.footer.copyright', { year: moment().year() })}
                </div>
                <div
                  className="comments__acceptance-message"
                  dangerouslySetInnerHTML={{ __html: this.getHtmlOfTAndCElement() }}
                />
              </div>
            </div>
          </Otherwise>
        </Choose>
      </div>
    );
  }
}

Comments.propTypes = {
  t: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      propertySlug: PropTypes.string,
    }).isRequired,
  }).isRequired,
  getPropertyCommentsStatus: PropTypes.string.isRequired,
  property: PropTypes.object,
  getPropertyComments: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }),
};

Comments.defaultProps = {
  t: () => {},
  matc: {
    params: {
      propertySlug: '',
    },
  },
  isGetPropertyIdle: false,
  getPropertyCommentsStatus: '',
  property: {},
  getPropertyComments: () => {},
  history: {
    push: () => {},
  },
  location: {
    search: '',
  },
};
